import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, Building2, Smartphone, Wallet, CheckCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
export function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Get payment details from navigation state
  const paymentDetails = location.state || {
    invoiceId: 'INV-2025-001234',
    amount: 500000,
    description: 'Iuran Keanggotaan Tahunan 2025'
  };
  const paymentMethods = [{
    id: 'bank_transfer',
    name: 'Transfer Bank',
    icon: Building2,
    description: 'BCA, Mandiri, BNI, BRI',
    fee: 0,
    popular: true
  }, {
    id: 'credit_card',
    name: 'Kartu Kredit/Debit',
    icon: CreditCard,
    description: 'Visa, Mastercard, JCB',
    fee: 2500,
    popular: false
  }, {
    id: 'e_wallet',
    name: 'E-Wallet',
    icon: Smartphone,
    description: 'GoPay, OVO, Dana, ShopeePay',
    fee: 1000,
    popular: true
  }, {
    id: 'virtual_account',
    name: 'Virtual Account',
    icon: Wallet,
    description: 'Semua bank via VA',
    fee: 0,
    popular: false
  }];
  const handlePayment = () => {
    if (!selectedMethod) {
      alert('Silakan pilih metode pembayaran');
      return;
    }
    setLoading(true);
    // Simulate payment processing
    setTimeout(() => {
      setLoading(false);
      navigate('/payment/gateway', {
        state: {
          ...paymentDetails,
          paymentMethod: selectedMethod
        }
      });
    }, 1000);
  };
  const selectedMethodData = paymentMethods.find(m => m.id === selectedMethod);
  const totalAmount = paymentDetails.amount + (selectedMethodData?.fee || 0);
  return <div className="min-h-screen bg-white" data-id="element-4183">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4184">
        {/* Header */}
        <div className="mb-8" data-id="element-4185">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-id="element-4186">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-4187" />
            Kembali
          </Button>
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-4188">
            Pembayaran
          </h1>
          <p className="text-neutral-600" data-id="element-4189">
            Pilih metode pembayaran yang Anda inginkan
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4190">
          {/* Payment Methods */}
          <div className="lg:col-span-2 space-y-6" data-id="element-4191">
            <Card data-id="element-4192">
              <CardHeader data-id="element-4193">
                <CardTitle data-id="element-4194">Metode Pembayaran</CardTitle>
              </CardHeader>
              <CardContent data-id="element-4195">
                <div className="space-y-3" data-id="element-4196">
                  {paymentMethods.map(method => <div key={method.id} className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedMethod === method.id ? 'border-atios-primary bg-atios-light' : 'border-neutral-200 hover:border-atios-primary/50'}`} onClick={() => setSelectedMethod(method.id)} data-id="element-4197">
                      <div className="flex items-center gap-4" data-id="element-4198">
                        <div className={`p-3 rounded-lg ${selectedMethod === method.id ? 'bg-atios-primary text-white' : 'bg-neutral-100 text-neutral-600'}`} data-id="element-4199">
                          <method.icon className="w-6 h-6" data-id="element-4200" />
                        </div>
                        <div className="flex-1" data-id="element-4201">
                          <div className="flex items-center gap-2 mb-1" data-id="element-4202">
                            <h3 className="font-semibold text-neutral-900" data-id="element-4203">
                              {method.name}
                            </h3>
                            {method.popular && <Badge className="bg-success text-white text-xs" data-id="element-4204">
                                POPULER
                              </Badge>}
                          </div>
                          <p className="text-sm text-neutral-600" data-id="element-4205">
                            {method.description}
                          </p>
                          {method.fee > 0 && <p className="text-xs text-neutral-500 mt-1" data-id="element-4206">
                              Biaya admin: Rp{' '}
                              {method.fee.toLocaleString('id-ID')}
                            </p>}
                        </div>
                        {selectedMethod === method.id && <CheckCircle className="w-6 h-6 text-atios-primary" data-id="element-4207" />}
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            <Alert variant="info" data-id="element-4208">
              <Info className="w-5 h-5" data-id="element-4209" />
              <div className="ml-3" data-id="element-4210">
                <p className="font-medium mb-1" data-id="element-4211">Informasi Pembayaran</p>
                <ul className="text-sm space-y-1" data-id="element-4212">
                  <li data-id="element-4213">• Pembayaran akan dikonfirmasi dalam 1x24 jam</li>
                  <li data-id="element-4214">• Simpan bukti pembayaran untuk referensi</li>
                  <li data-id="element-4215">• Hubungi admin jika ada kendala</li>
                </ul>
              </div>
            </Alert>
          </div>

          {/* Payment Summary */}
          <div className="lg:col-span-1" data-id="element-4216">
            <Card className="sticky top-8" data-id="element-4217">
              <CardHeader data-id="element-4218">
                <CardTitle data-id="element-4219">Ringkasan Pembayaran</CardTitle>
              </CardHeader>
              <CardContent data-id="element-4220">
                <div className="space-y-4" data-id="element-4221">
                  <div data-id="element-4222">
                    <p className="text-sm text-neutral-600 mb-1" data-id="element-4223">Invoice</p>
                    <p className="font-medium text-neutral-900" data-id="element-4224">
                      {paymentDetails.invoiceId}
                    </p>
                  </div>
                  <div data-id="element-4225">
                    <p className="text-sm text-neutral-600 mb-1" data-id="element-4226">Deskripsi</p>
                    <p className="font-medium text-neutral-900" data-id="element-4227">
                      {paymentDetails.description}
                    </p>
                  </div>
                  <div className="border-t border-neutral-200 pt-4 space-y-2" data-id="element-4228">
                    <div className="flex justify-between" data-id="element-4229">
                      <span className="text-neutral-600" data-id="element-4230">Subtotal</span>
                      <span className="font-medium text-neutral-900" data-id="element-4231">
                        Rp {paymentDetails.amount.toLocaleString('id-ID')}
                      </span>
                    </div>
                    {selectedMethodData && selectedMethodData.fee > 0 && <div className="flex justify-between" data-id="element-4232">
                        <span className="text-neutral-600" data-id="element-4233">Biaya Admin</span>
                        <span className="font-medium text-neutral-900" data-id="element-4234">
                          Rp {selectedMethodData.fee.toLocaleString('id-ID')}
                        </span>
                      </div>}
                    <div className="flex justify-between pt-2 border-t border-neutral-200" data-id="element-4235">
                      <span className="text-lg font-semibold text-neutral-900" data-id="element-4236">
                        Total
                      </span>
                      <span className="text-2xl font-bold text-atios-primary" data-id="element-4237">
                        Rp {totalAmount.toLocaleString('id-ID')}
                      </span>
                    </div>
                  </div>
                  <Button onClick={handlePayment} disabled={!selectedMethod || loading} className="w-full" size="lg" data-id="element-4238">
                    {loading ? 'Memproses...' : 'Lanjutkan Pembayaran'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}